import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { permissionApi } from '@/entities/Permission';
import { useBranchLogo } from '@/entities/Branch';
import { waitForPrintAssets } from '@/shared/lib/print';
import type {
  FunctionalActivityPermissionsList,
  PermissionsListCriterion,
  PermissionsListData,
  PositionPermissionsList,
} from './AdminPermissionsListPage.types';

const PRINT_CONTENT_ID = 'permissions-report-content';

export function usePermissionsListPage() {
  const route = useRoute();
  const { resolvePrintableLogoSrc } = useBranchLogo();

  const loading = ref(false);
  const error = ref<string | null>(null);
  const reportData = ref<PermissionsListData | null>(null);

  const reportDate = computed(() => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    return `${day}/${month}/${year}`;
  });

  const criterion = computed<PermissionsListCriterion | null>(() => {
    const value = route.query.criterion;
    if (value === 'registrationNumber' || value === 'position' || value === 'functionalActivity') {
      return value;
    }

    return null;
  });

  const registrationReport = computed(() =>
    reportData.value?.criterion === 'registrationNumber' ? reportData.value.report : null
  );

  const positionReport = computed<PositionPermissionsList | null>(() =>
    reportData.value?.criterion === 'position' ? reportData.value.report : null
  );

  const functionalActivityReport = computed<FunctionalActivityPermissionsList | null>(() =>
    reportData.value?.criterion === 'functionalActivity' ? reportData.value.report : null
  );

  const fetchReport = async () => {
    loading.value = true;
    error.value = null;
    reportData.value = null;

    try {
      if (!criterion.value) {
        throw new Error('Critério de listagem inválido. Retorne ao modal e tente novamente.');
      }

      if (criterion.value === 'registrationNumber') {
        const registrationNumber = String(route.query.registrationNumber ?? '').trim();
        if (!registrationNumber) {
          throw new Error('Matrícula não informada. Retorne ao modal e tente novamente.');
        }

        const report = await permissionApi.getRegistrationPermissions(registrationNumber);
        reportData.value = {
          criterion: 'registrationNumber',
          report: {
            ...report,
            functionalActivities: normalizeFunctionalActivities(report.functionalActivities),
            permissionsByCategory: Array.isArray(report.permissionsByCategory)
              ? report.permissionsByCategory
              : [],
          },
        };
        return;
      }

      if (criterion.value === 'position') {
        const positionIds = parseIdList(route.query.positionIds);
        if (positionIds.length === 0) {
          throw new Error('Cargo não informado. Retorne ao modal e tente novamente.');
        }

        reportData.value = {
          criterion: 'position',
          report: await permissionApi.getBulkPositionPermissions(positionIds),
        };
        return;
      }

      const functionalActivityIds = parseIdList(route.query.functionalActivityIds);
      if (functionalActivityIds.length === 0) {
        throw new Error('Atividade funcional não informada. Retorne ao modal e tente novamente.');
      }

      reportData.value = {
        criterion: 'functionalActivity',
        report: await permissionApi.getBulkFunctionalActivityPermissions(functionalActivityIds),
      };
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Erro ao carregar relatório de permissões';
    } finally {
      loading.value = false;
    }
  };

  const handlePrint = async () => {
    const printContent = document.getElementById(PRINT_CONTENT_ID);
    if (!printContent) {
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      return;
    }

    const printableContent = printContent.cloneNode(true) as HTMLElement;
    const logo = printableContent.querySelector('[data-print="header-table"] img');
    logo?.setAttribute('src', await resolvePrintableLogoSrc());

    printWindow.document.write(`
      <html>
        <head>
          <title>Lista de Permissões</title>
          <style>
            * { box-sizing: border-box; }
            body {
              margin: 24px;
              font-family: Outfit, Arial, sans-serif;
              color: #2f2f2f;
              background: #ffffff;
            }
            img {
              max-width: 100%;
              height: auto;
            }
            [data-print="header-table"] img {
              max-width: 170px;
              max-height: 84px;
            }
            [data-print="button"] {
              display: none !important;
            }
            [data-print="divider"] {
              margin: 24px 0;
              border-top: 1px solid #e3e3e3;
            }
            [data-print="header-table"] {
              width: 100%;
              margin-bottom: 24px;
              border-collapse: collapse;
            }
            [data-print="header-table"] td {
              border: 1px solid #9dabb8;
              text-align: center;
              vertical-align: middle;
            }
            [data-print="panel"] {
              overflow: hidden;
              border: 1px solid #e3e3e3;
              border-radius: 16px;
              background: #ffffff;
            }
            [data-print="panel"]:not(:last-child) {
              margin-bottom: 16px;
            }
            [data-print="data-table"] {
              width: 100%;
              border-collapse: collapse;
            }
            [data-print="data-table"] thead {
              display: table-row-group;
            }
            [data-print="data-table"] th,
            [data-print="data-table"] td {
              padding: 10px 14px;
              text-align: left;
              font-size: 14px;
              line-height: 18px;
            }
            [data-print="data-table"] thead tr {
              border-bottom: 1px solid #e9e9e9;
            }
            [data-print="data-table"] tbody tr {
              border-top: 1px solid #f0f0f0;
            }
            [data-print="permissions-grid"] {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
              gap: 16px;
              padding: 12px 14px 14px;
              border: 0;
              border-radius: 0;
              background: #ffffff;
            }
            [data-print="category"] {
              display: flex;
              min-width: 0;
              flex-direction: column;
              gap: 8px;
            }
            [data-print="category-items"] {
              display: flex;
              flex-direction: column;
              gap: 6px;
              align-items: flex-start;
            }
          </style>
        </head>
        <body>
          ${printableContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    await waitForPrintAssets(printWindow);
    printWindow.print();
  };

  return {
    loading,
    error,
    reportDate,
    registrationReport,
    positionReport,
    functionalActivityReport,
    fetchReport,
    handlePrint,
  };
}

function normalizeFunctionalActivities(
  functionalActivities:
    | Array<{
        id: number;
        name: string;
      }>
    | null
    | undefined
) {
  return Array.isArray(functionalActivities) ? functionalActivities : [];
}

function parseIdList(value: unknown): number[] {
  const rawValue = Array.isArray(value) ? value.join(',') : String(value ?? '');

  return rawValue
    .split(',')
    .map((item) => Number(item.trim()))
    .filter((item) => Number.isFinite(item) && item > 0);
}
